import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBids } from '../hooks/useBids';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import './CSS/auth.css';

const AddBidToCart = () => {
  const { t } = useTranslation(['bids', 'common']);
  const { bidId } = useParams();
  const navigate = useNavigate();
  const { addBidToCartAsync } = useBids();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const addToCart = async () => {
      if (!bidId) {
        setStatus('error');
        setErrorMessage(t('invalidBidId', { defaultValue: 'Invalid bid ID' }));
        return;
      }

      try {
        // Validate bidId format (MongoDB ObjectId: 24 hex characters)
        const bidIdStr = String(bidId).trim();
        if (bidIdStr.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(bidIdStr)) {
          setStatus('error');
          setErrorMessage(t('invalidBidIdFormat', { defaultValue: 'Invalid bid ID format' }));
          return;
        }

        // Add bid to cart
        await addBidToCartAsync({
          bidId: bidIdStr,
          size: null,
          color: null,
        });

        // Success - redirect to cart after a short delay
        setStatus('success');
        setTimeout(() => {
          navigate('/carte');
        }, 2000);
      } catch (error) {
        console.error('Error adding bid to cart:', error);
        setStatus('error');
        setErrorMessage(error.message || t('failedToAddBidToCart', { defaultValue: 'Failed to add bid to cart. Please try again.' }));
      }
    };

    addToCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId, addBidToCartAsync, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        {status === 'loading' && (
          <div className="auth-content">
            <Loader2 className="auth-loading-icon spin" size={48} />
            <h2 className="auth-title">{t('addingToCart', { defaultValue: 'Adding to Cart...' })}</h2>
            <p className="auth-text">{t('pleaseWaitAddingBid', { defaultValue: 'Please wait while we add your accepted bid to your cart.' })}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="auth-content">
            <CheckCircle className="auth-success-icon" size={64} color="var(--color-primary)" />
            <h2 className="auth-title">{t('success', { defaultValue: 'Success!', ns: 'common' })}</h2>
            <p className="auth-success-text">
              {t('bidAddedToCart', { defaultValue: 'Your accepted bid has been added to your cart.' })}
            </p>
            <p className="auth-hint">{t('redirectingToCart', { defaultValue: 'Redirecting to your cart...' })}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="auth-content">
            <XCircle className="auth-error-icon" size={64} color="#dc3545" />
            <h2 className="auth-title">{t('error', { defaultValue: 'Error', ns: 'common' })}</h2>
            <p className="auth-error-text">{errorMessage}</p>
            <div className="auth-actions">
              <button
                onClick={() => navigate('/profile?tab=bids')}
                className="auth-resend-btn"
              >
                {t('viewMyBids', { defaultValue: 'View My Bids' })}
              </button>
              <button
                onClick={() => navigate('/carte')}
                className="auth-resend-btn"
                style={{ marginLeft: '10px' }}
              >
                {t('goToCart', { defaultValue: 'Go to Cart' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBidToCart;

